import offlineService from './offline';

const TICKET_API_URL = '/api/tickets';

const isMutation = (method) => ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

class TicketService {
  getToken() {
    return localStorage.getItem('auth_token');
  }

  // Helper: build FormData from object (skip null/undefined)
  buildFormData(data) {
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        fd.append(key, value);
      }
    });
    return fd;
  }

  // Helper: check if value is a File object
  hasFile(data) {
    return Object.values(data).some(v => v instanceof File);
  }

  async request(endpoint, options = {}) {
    const url = `${TICKET_API_URL}${endpoint}`;
    const token = this.getToken();

    const isFormData = options.body instanceof FormData;

    const config = {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
      ...options,
    };

    // Don't stringify FormData
    if (isFormData) {
      delete config.headers['Content-Type'];
    } else if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        // Auto-logout jika token expired
        if (response.status === 401 && data.error && data.error.includes('Token expired')) {
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // Cache GET responses for offline
      if (!isMutation(options.method)) {
        offlineService.setCached(url, data).catch(() => {});
      }

      return data;
    } catch (error) {
      // Offline: queue mutations, serve cached GET
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const method = options.method || 'GET';
        if (isMutation(method)) {
          const bodyData = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
          await offlineService.queueMutation(method, url, bodyData);
          console.warn('📱 Offline: ticket mutation queued');
          return { _offline: true, _queued: true, message: 'Tiket akan dikirim saat koneksi kembali' };
        } else {
          const cached = await offlineService.getCached(url);
          if (cached) {
            console.warn('📱 Offline: serving cached tickets');
            return cached;
          }
        }
      }
      console.error('Ticket API request failed:', error);
      throw error;
    }
  }

  // Get all tickets (admin)
  async getTickets(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/?${queryString}`);
  }

  // Get my tickets (user)
  async getMyTickets(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/mine?${queryString}`);
  }

  // Get single ticket
  async getTicket(id) {
    return this.request(`/${id}`);
  }

  // Create ticket
  async createTicket(ticketData) {
    const body = this.hasFile(ticketData) ? this.buildFormData(ticketData) : ticketData;
    return this.request('/', {
      method: 'POST',
      body,
    });
  }

  // Update ticket
  async updateTicket(id, ticketData) {
    const body = this.hasFile(ticketData) ? this.buildFormData(ticketData) : ticketData;
    return this.request(`/${id}`, {
      method: 'PUT',
      body,
    });
  }

  // Update ticket status (admin)
  async updateTicketStatus(id, status, resolution = '') {
    return this.request(`/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, resolution }),
    });
  }

  // Assign ticket (admin)
  async assignTicket(id, userData) {
    return this.request(`/${id}/assign`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // Add note
  async addNote(id, text) {
    return this.request(`/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // Delete ticket (admin)
  async deleteTicket(id) {
    return this.request(`/${id}`, {
      method: 'DELETE',
    });
  }

  // Get stats
  async getStats() {
    return this.request('/stats');
  }
}

export const ticketService = new TicketService();
export default ticketService;
